import { useQueryClient } from '@tanstack/react-query';
import { CheckIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CiGlobe } from 'react-icons/ci';
import { TbChevronDown } from 'react-icons/tb';
import { useSearchParams } from 'react-router';

import { useGlobalStore } from '@cccom/config/global-store';
import { useDate } from '@cccom/shared/settings';
import { Button } from '@cccom/ui/src/components/shadcn/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@cccom/ui/src/components/shadcn/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cccom/ui/src/components/shadcn/popover';
import { cn } from '@cccom/ui/src/utils';

import { useSetCurrentSite, useSites } from '../data-access/sites.api';
import { SiteSelectorModal } from './SiteSelectorModal';

export function SiteSelector() {
  const [t] = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { timezoneOffset } = useDate();
  const queryClient = useQueryClient();

  const setIsSwitchingSite = useGlobalStore(
    (state) => state.setIsSwitchingSite
  );
  const { data: sites } = useSites();
  const { mutateAsync: saveCurrentSite } = useSetCurrentSite();
  const currentSite = useGlobalStore((state) => state.currentSite);
  const setCurrentSite = useGlobalStore((state) => state.setCurrentSite);

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');

  const getSiteById = useCallback(
    (id: string) => sites?.find((site) => site?.entityId === id),
    [sites]
  );

  useEffect(() => setValue(currentSite?.entityId ?? ''), [currentSite]);

  const handleSiteSelect = useCallback(
    async (_siteId: string) => {
      const selectedSite = getSiteById(_siteId);
      if (!selectedSite) return;
      setValue(selectedSite.entityId);
      await saveCurrentSite(selectedSite.entityId); // Persist selection
      setCurrentSite(selectedSite); // Update site in global store
      setIsSwitchingSite(true); // Indicate site switching in progress for AuthLoader
      queryClient.invalidateQueries(); // Trigger refetch with new site
      setOpen(false);
    },
    [
      queryClient,
      getSiteById,
      saveCurrentSite,
      setCurrentSite,
      setIsSwitchingSite,
    ]
  );

  useEffect(() => {
    // change site based on x-site param if it differs from current site
    const xsite = searchParams.get('x-site');
    if (xsite && xsite !== currentSite?.entityId) handleSiteSelect(xsite);
  }, [currentSite, searchParams, handleSiteSelect, setSearchParams]);

  return (
    <>
      <SiteSelectorModal />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            role="combobox"
            aria-expanded={open}
            className="flex h-full w-[240px] items-center justify-between text-left"
          >
            <CiGlobe className="!size-7 text-gray-500" />
            <span className="flex min-w-0 flex-1 flex-col items-start text-nowrap text-gray-600">
              <h4 className="w-full overflow-hidden text-ellipsis text-sm">
                {value ? getSiteById(value)?.name : t('sites.select_site')}
              </h4>
              <h4 className="w-full overflow-hidden text-ellipsis text-[10px]">
                {timezoneOffset}
              </h4>
            </span>
            <TbChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[240px] p-0">
          <Command
            value={value}
            filter={(id, search) => {
              const selectedSite = getSiteById(id)?.name.toLowerCase();
              if (selectedSite?.includes(search.toLowerCase())) return 1;
              return 0;
            }}
          >
            <CommandInput
              placeholder={t('sites.search_site')}
              className="ps-2"
            />
            <CommandList>
              <CommandEmpty>{t('sites.no_site_found')}</CommandEmpty>
              <CommandGroup>
                {sites?.map((site) => (
                  <CommandItem
                    className="text-sm hover:cursor-pointer"
                    key={site.entityId}
                    value={site.entityId}
                    onSelect={async (siteId) => {
                      if (siteId === value) return;
                      await handleSiteSelect(siteId);
                      window.location.reload(); // Reload to reset app state
                    }}
                  >
                    <CheckIcon
                      className={cn(
                        'mr-2 h-4 w-4',
                        value === site?.entityId ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                    {site.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  );
}
